import ms from 'ms';
import ora from 'ora';
import WebSocket from 'ws';
import * as fs from 'node:fs';
import * as path from 'node:path';
import * as ChromeLauncher from 'chrome-launcher';
import * as config from './commons/config';
import * as prepareUtils from './commons/prepareRunnerAndTestimStartUtils';
import { ArgError } from './errors';
import { downloadAndInstallChromium, CHROMIUM_VERSION } from './chromiumInstaller';
import { downloadAndSave, unzipFile, getCdpAddressForHost, TESTIM_BROWSER_DIR, promiseFromCallback, doesPathExist } from './utils';
import { getDisabledChromeFeatures } from './commons/testimDesiredCapabilitiesBuilder';
import { generateWebSocketOptions } from './utils/network';
import type { WebDriver } from './player/webdriver';
import type { AgentModeOptions } from './runOptions';

const LOG_LEVEL = config.WEBDRIVER_DEBUG ? 'verbose' : 'silent';
const EXTENSION_CACHE_TIME = ms('1h');
const USER_DATA_DIR = path.join(TESTIM_BROWSER_DIR, 'profile');

// https://github.com/bayandin/chromedriver/blob/5013f2124888c50fff15dc2ff8287288f780b046/chrome_launcher.cc#L105
const CHOMEDRIVER_DEVTOOLS_ACTIVE_PORT_FILENAME = 'DevToolsActivePort';
const CHOMEDRIVER_DEVTOOLS_ACTIVE_PORT_FILE_PATH = path.join(USER_DATA_DIR, CHOMEDRIVER_DEVTOOLS_ACTIVE_PORT_FILENAME);

export interface StandaloneBrowser { webdriverApi: WebDriver | { port: number; pid: number; cdpUrl: string } }

export async function runAgentMode(options: AgentModeOptions) {
    let testimStandaloneBrowser: StandaloneBrowser | undefined;

    await prepareUtils.preparePlayer(options.playerLocation, options.canary);

    if (options.startTestimBrowser) {
        await getRidOfPossiblyRunningChromeWithOurDataDir();
        try {
            // Consider moving that into the agent server and add endpoint to start browser?
            testimStandaloneBrowser = await startTestimStandaloneBrowser(options);
        } catch (e) {
            if (e?.message?.includes('user data directory is already in use')) {
                throw new ArgError('Please close all chrome browsers that were opened with "testim start" and try again');
            }
            throw e;
        }
    }

    const agentServer = await import('./agent/server');

    if (testimStandaloneBrowser?.webdriverApi) {
        // if we're starting the agent here, pre-load the sessionPlayer so it loads faster
        // on first play
        const LOAD_PLAYER_DELAY = 6000;
        setTimeout(() => {
            setTimeout(() => require('./player/seleniumTestPlayer'));
        }, LOAD_PLAYER_DELAY);
    }

    return agentServer.init(options);
}

let startedWithStart = false;

export function getStartedWithStart() {
    return startedWithStart;
}

function isPidRunning(pid: number) {
    try {
        return process.kill(pid, 0);
    } catch {
        return false;
    }
}

async function startFixedVersionChromium(options: AgentModeOptions, extensionBase64: string | null, downloadedExtensionPathUnzipped: string) {
    const CHROMIUM_PROCESS_INFO_FILE = path.join(TESTIM_BROWSER_DIR, `chrome-${CHROMIUM_VERSION}-process`);
    const CHECK_CHROMIUM_RUNNING_INTERVAL = 3000;

    const onBrowserClosed = () => {
        fs.rmSync(CHROMIUM_PROCESS_INFO_FILE, { recursive: true });
        // eslint-disable-next-line no-console
        console.log('\n\nBrowser session ended');
        process.exit(0);
    };

    if (await doesPathExist(CHROMIUM_PROCESS_INFO_FILE)) {
        // @ts-expect-error JSON.parse handles buffers as well, the types are not smart enough :(
        const processInfo = JSON.parse(fs.readFileSync(CHROMIUM_PROCESS_INFO_FILE)) as { port: number; pid: number; cdpUrl: string };
        if (isPidRunning(processInfo.pid)) { // if a previous instance of our browser is still running, use it and exit if it does
            const monitorPidForExit = (): NodeJS.Timeout => (isPidRunning(processInfo.pid) ? setTimeout(monitorPidForExit, CHECK_CHROMIUM_RUNNING_INTERVAL) : onBrowserClosed());
            monitorPidForExit();
            return {
                webdriverApi: processInfo,
            };
        }
    }
    const chromeBinary = await downloadAndInstallChromium();

    if (!(await doesPathExist(USER_DATA_DIR))) {
        await fs.promises.mkdir(USER_DATA_DIR, { recursive: true });
    }
    const capabilities = buildSeleniumOptions(USER_DATA_DIR, extensionBase64, downloadedExtensionPathUnzipped, chromeBinary);
    const chromeFlags = [
        ...capabilities.desiredCapabilities.chromeOptions.args,
        ...ChromeLauncher.Launcher.defaultFlags().filter(flag => ![
            '--disable-extensions',
            '--disable-component-extensions-with-background-pages', // causes google connect to disallow some accounts (eg gmail accounts get a "This browser or app may not be secure" error)
        ].includes(flag)),
    ];
    // Chromium needs API keys to communicate with google APIs (https://www.chromium.org/developers/how-tos/api-keys/)
    // These are keys that were included in some chrome builds. They are widely distributed on the internet, their inclusion here is approved by ProdSec.
    const envVars = {
        GOOGLE_API_KEY: 'AIzaSyCkfPOPZXDKNn8hhgu3JrA62wIgC93d44k', // deepcode ignore HardcodedNonCryptoSecret: See comment above
        GOOGLE_DEFAULT_CLIENT_ID: '811574891467.apps.googleusercontent.com',
        GOOGLE_DEFAULT_CLIENT_SECRET: 'kdloedMFGdGla2P1zacGjAQh', // deepcode ignore HardcodedNonCryptoSecret: See comment above
    };
    const appUrl = options.extensionPath ? 'http://localhost:3000/app/' : new URL('?startMode=true', config.EDITOR_URL || config.DEFAULT_EDITOR_URL).href;
    const chrome = await ChromeLauncher.launch({ chromeFlags, startingUrl: appUrl, ignoreDefaultFlags: true, userDataDir: USER_DATA_DIR, chromePath: chromeBinary, envVars });
    const processInfo = { port: chrome.port, pid: chrome.pid, cdpUrl: await getCdpAddressForHost(`localhost:${chrome.port}`) };
    fs.writeFileSync(CHROMIUM_PROCESS_INFO_FILE, JSON.stringify(processInfo));
    chrome.process.once('exit', onBrowserClosed);
    chrome.process.once('close', onBrowserClosed);
    return {
        webdriverApi: processInfo,
    };
}

async function startTestimStandaloneBrowser(options: AgentModeOptions) {
    const fullExtensionUrl = `${config.EXTENSION_SP_S3_HOST}/testim-full-master.zip`;
    const extensionFilename = path.basename(fullExtensionUrl);

    const downloadedExtensionPath = path.join(TESTIM_BROWSER_DIR, extensionFilename);
    const downloadedExtensionPathUnzipped = path.join(TESTIM_BROWSER_DIR, `${extensionFilename}__unzipped__`);

    let shouldDownloadExtension = !(options.ext || options.extensionPath);

    if (shouldDownloadExtension && await doesPathExist(downloadedExtensionPath)) {
        const stat = await fs.promises.stat(downloadedExtensionPath);
        shouldDownloadExtension = (Date.now() - EXTENSION_CACHE_TIME > stat.mtimeMs);
    }
    await fs.promises.mkdir(TESTIM_BROWSER_DIR, { recursive: true });

    if (shouldDownloadExtension) {
        const spinner = ora('Downloading Testim Editor').start();
        await downloadAndSave(fullExtensionUrl, downloadedExtensionPath);

        try {
            // Ensure the zip is ok
            await unzipFile(downloadedExtensionPath, downloadedExtensionPathUnzipped);
        } catch (e) {
            // The downloaded zip is corrupted, try re download once
            await fs.promises.rm(downloadedExtensionPath, { recursive: true, force: true });
            await downloadAndSave(fullExtensionUrl, downloadedExtensionPath);
            try {
                await unzipFile(downloadedExtensionPath, downloadedExtensionPathUnzipped);
            } catch (err) {
                // zip is bad again.
                await fs.promises.rm(downloadedExtensionPath, { recursive: true, force: true });
                spinner.fail('Failed to download Testim Editor');
                throw new Error('Failed to download Testim Editor');
            }
        } finally {
            if (!options.downloadBrowser) {
                await fs.promises.rm(downloadedExtensionPathUnzipped, { recursive: true, force: true });
            }
        }

        spinner.succeed();
    }

    const extensionBase64 = options.extensionPath ? null : (await fs.promises.readFile(options.ext || downloadedExtensionPath)).toString('base64');
    if (options.downloadBrowser) {
        return await startFixedVersionChromium(options, extensionBase64, downloadedExtensionPathUnzipped);
    }
    await prepareUtils.prepareChromeDriver(
        { projectId: options.project },
        // @ts-expect-error not clear where chromeBinaryLocations comes from
        { chromeBinaryLocation: options.chromeBinaryLocations },
    );

    // @ts-expect-error not clear where chromeBinaryLocations comes from
    const seleniumOptions = buildSeleniumOptions(USER_DATA_DIR, extensionBase64, options.extensionPath, options.chromeBinaryLocations);

    const { WebDriver } = require('./player/webdriver') as typeof import('./player/webdriver');
    const { SeleniumPerfStats } = require('./commons/SeleniumPerfStats') as typeof import('./commons/SeleniumPerfStats');

    const webdriverApi = new WebDriver();
    webdriverApi.seleniumPerfStats = new SeleniumPerfStats();

    // starts chrome via selenium, note this is intentionally initClient and not init to bypass desired capabilities parsing
    const webdriverInitResponse = await webdriverApi.initClient(seleniumOptions as any);

    // example values from webdriverInitResponse
    // webdriverInitResponse.sessionId
    // webdriverInitResponse.value["goog:chromeOptions"].debuggerAddress
    // webdriverInitResponse.chrome.userDataDir;

    // require user token, so we can't use it for now
    // const { getEditorUrl } = require('./commons/testimServicesApi');

    startedWithStart = true;
    const appUrl = `${options.extensionPath ? 'http://localhost:3000/app/' : config.EDITOR_URL || config.DEFAULT_EDITOR_URL}?startMode=true`;

    await webdriverApi.url(appUrl);
    // save the initial URL we navigated to so we don't consider it the AuT
    Object.assign(webdriverApi, { initialUrl: appUrl });
    try {
        //TODO(Benji) do we want this to be exactly getCdpAddressForHost or should this fail less gracefully indicating the agent did not start correctly?
        // @ts-expect-error something here is off. the type of webdriverInitResponse is void
        webdriverApi.cdpUrl = await getCdpAddressForHost(webdriverInitResponse.value['goog:chromeOptions'].debuggerAddress);
    } catch (e) {
        // ignore error
    }

    return {
        webdriverApi,
    };
}

function buildSeleniumOptions(userDataDir: string, fullExtensionPath: string | null, unpackedExtensionPath: string, chromeBinaryPath: string) {
    const extensions = fullExtensionPath ? [fullExtensionPath] : [];
    const args = [
        `--user-data-dir=${userDataDir}`, // crashes chromium, re-enable if using chrome
        '--log-level=OFF',
        '--silent-debugger-extension-api',
        '--no-first-run',
    ];
    if (unpackedExtensionPath) {
        args.push(`--load-extension=${unpackedExtensionPath}`);
    }

    try {
        const disableChromeFeatures = getDisabledChromeFeatures();
        if (disableChromeFeatures.length) {
            disableChromeFeatures.forEach((feature: string) => args.push(`--disable-features=${feature}`));
        }
    } catch {
        // if we couldn't parse the flag, we don't want to block the run, silently fail into oblivion
    }

    return {
        logLevel: LOG_LEVEL,
        desiredCapabilities: {
            chromeOptions: {
                args,
                extensions,
                binary: chromeBinaryPath,
            },
            browserName: 'chrome',
        },
        host: 'localhost',
        port: 9515, // chromedriver port
    };
}

/**
 * Overview of what we do here:
 * we check if CHOMEDRIVER_DEVTOOLS_ACTIVE_PORT_FILE_PATH is exists
 * If we can read the port & CDP url info from that file.
 * If we can read, but we can't send http request to the devtools server we assume it's closed and just delete the file
 * If we can read and send HTTP request, send CDP command of Browser.close to CDP, and delete the file.
 * If any of these fails we assume we couldn't kill the browser, and the user will get the "close the running chromes errors down the line"
 */
async function getRidOfPossiblyRunningChromeWithOurDataDir() {
    if (!await doesPathExist(CHOMEDRIVER_DEVTOOLS_ACTIVE_PORT_FILE_PATH)) {
        return;
    }

    try {
        const { webSocketDebuggerUrl } = await readAndValidateChromedriverDevToolsActivePortFile();
        await tryToCloseBrowserWithCDPUrl(webSocketDebuggerUrl);
        await fs.promises.unlink(CHOMEDRIVER_DEVTOOLS_ACTIVE_PORT_FILE_PATH);
    } catch (e) {
        // chrome is probably not really running, we are cool
        if (e && e.message === 'unable to connect to devtools http server') {
            await fs.promises.unlink(CHOMEDRIVER_DEVTOOLS_ACTIVE_PORT_FILE_PATH);
        }
    }
}

async function readAndValidateChromedriverDevToolsActivePortFile() {
    /**
     * file content example:

    58938
    /devtools/browser/d4290379-ec08-4d03-a41a-ab9d9d4c36ac

    */

    const fileContent = await fs.promises.readFile(CHOMEDRIVER_DEVTOOLS_ACTIVE_PORT_FILE_PATH, { encoding: 'utf8' });

    const [portLine, browserCDPURLLine] = fileContent.split('\n').map(line => line.trim());

    const port = Number.parseInt(portLine, 10);

    if (!Number.isInteger(port) || port < 1 || port > 65535) {
        throw new Error('invalid port number');
    }

    if (!browserCDPURLLine.startsWith('/devtools/browser/')) {
        throw new Error('invalid devtools browser url');
    }

    const webSocketDebuggerUrl = await getCdpAddressForHost(`localhost:${port}`, 500);
    // invariant check
    if (!webSocketDebuggerUrl.endsWith(browserCDPURLLine)) {
        throw new Error('invariant webSocketDebuggerUrl miss match');
    }

    return {
        port,
        webSocketDebuggerUrl,
    };
}

async function tryToCloseBrowserWithCDPUrl(webSocketDebuggerUrl: string | URL, timeout = 100) {
    const websocketConnection = await wsConnectAndOpen(webSocketDebuggerUrl, timeout);

    return promiseFromCallback<Error | undefined, never>(cb => {
        websocketConnection.send(JSON.stringify({
            id: 0,
            method: 'Browser.close',
        }), cb);
    });
}

async function wsConnectAndOpen(webSocketDebuggerUrl: string | URL, timeout = 100) {
    const options = generateWebSocketOptions({ timeout });
    const websocket = new WebSocket(webSocketDebuggerUrl, options);

    const openPromise = promiseFromCallback((cb) => {
        websocket.once('open', cb);
    }).then(() => {
        websocket.removeAllListeners();
    });

    const errorPromise = promiseFromCallback((cb) => {
        websocket.once('error', cb);
    }).catch(() => {
        websocket.close();
        websocket.removeAllListeners();
    });

    return Promise.race([openPromise, errorPromise]).then(() => websocket);
}

